/**
 * Module UI registry — per-module and per-lesson metadata that lives outside
 * the player JSON (icon, colours, competencies, lesson type, etc.).
 *
 * Adding a new module: author the JSON in content/modules/, then add one entry
 * here keyed by the JSON's `id` field. No other application code changes needed.
 */

export interface SubModuleMeta {
  /** Guided walkthrough, free-run simulation, or orientation tour. */
  type: 'guided-tutorial' | 'unguided-sim' | 'tour'
  /** One-sentence description shown on the lesson card and detail screen. */
  description: string
  /** Bullet points shown in the "What You'll Learn" section of LessonDetail. */
  competencies: string[]
  /** Minimum score to pass, shown as a percentage. */
  passingScore: number
  /** e.g. "Single Attempt Certification" or "Up to 2 Attempts". */
  attempts: string
  /** Approximate duration in minutes — used on lesson cards. */
  estimatedMinutes: number
  /** Shown as a Required Training card on the dashboard when true. */
  required: boolean
  /** Due label shown on the required training card, or null if not time-bound. */
  dueLabel: string | null
  /** Tag chips shown on the required training card (e.g. ["Mandatory"]). */
  tags: string[]
}

export interface ModuleRegistryEntry {
  /** Matches the `:id` param in the /module/:id route (e.g. "ordering"). */
  navId: string
  /** When true the module is placeholder content — shown as Pending, not navigable. */
  comingSoon?: boolean
  /** Short descriptor shown under the module title on dashboard cards. */
  subtitle: string
  /** Key into the ICON_MAP in screen components. */
  icon: string
  /** Key into the colour maps in screen components. */
  accentColour: string
  /** Badge text shown on the module detail hero (e.g. "Module 01 • Ordering"). */
  moduleTag: string
  /** Key into MODULE_COLOUR_CLASSES used by LessonDetailScreen. */
  moduleColour: string
  /** Multi-sentence description shown on the module detail hero card. */
  moduleDescription: string
  /** Metadata keyed by subModule id (matches `subModule.id` in the JSON). */
  subModules: Record<string, SubModuleMeta>
}

function lesson(
  description: string,
  competencies: string[],
  estimatedMinutes: number,
  overrides?: Partial<SubModuleMeta>,
): SubModuleMeta {
  return {
    type: 'guided-tutorial',
    description,
    competencies,
    passingScore: 80,
    attempts: 'Single Attempt Certification',
    estimatedMinutes,
    required: true,
    dueLabel: null,
    tags: [],
    ...overrides,
  }
}

function optionalLesson(
  description: string,
  competencies: string[],
  estimatedMinutes: number,
  overrides?: Partial<SubModuleMeta>,
): SubModuleMeta {
  return lesson(description, competencies, estimatedMinutes, { required: false, ...overrides })
}

function tour(description: string, estimatedMinutes: number): SubModuleMeta {
  return {
    type: 'tour',
    description,
    competencies: [],
    passingScore: 0,
    attempts: '',
    estimatedMinutes,
    required: false,
    dueLabel: null,
    tags: [],
  }
}

export const MODULE_REGISTRY: Record<string, ModuleRegistryEntry> = {
  'ordering-essentials': {
    navId: 'ordering',
    subtitle: 'Automated Replenish',
    icon: 'ShoppingCart',
    accentColour: 'blue',
    moduleTag: 'Module 01 • Ordering',
    moduleColour: 'blue',
    moduleDescription:
      'Understand how to build and submit purchase orders, create Quick Purchase Orders at the dock, resolve shortfalls, and look up delivery history.',
    subModules: {
      'ordering-sandbox-tour': tour(
        'A quick orientation to the ordering sandbox — explore the key screens before you begin your first lesson.',
        2,
      ),
      'ordering-lesson-1': lesson(
        'Learn the 3 ways to start creating your orders:\n1. Going to Cart directly\n2. Searching for SKU in Articlepedia and then adding to Cart\n3. Scanning barcode of the SKUs and adding to Cart, and submitting them.',
        [
          'Adding items to Cart from Search in Cart Icon, via Search Bar or + Button',
          'Going to Articlepedia by scanning product barcode from scan button or from Bottom Menu',
          'Adding items to Cart through Articlepedia',
        ],
        15,
        { dueLabel: 'Due in 2 days', tags: ['Mandatory'] },
      ),
      'ordering-lesson-2': lesson(
        'Learn how to review and amend your orders before submitting them.',
        [
          'Viewing your Draft Orders (at Cart)',
          'Use Search & Filter to find items in a growing cart',
          'Deleting your Draft Orders',
          'Amending your Draft Orders',
          'Submitting your Orders by WH or Vendor or by ALL',
        ],
        22,
        { attempts: 'Up to 2 Attempts', dueLabel: 'Due in 4 days', tags: ['Dock Safety'] },
      ),
      'ordering-lesson-3': lesson(
        'Learn how to create a Quick Purchase Order at the dock using two entry methods: scanning the supplier QR code, or entering supplier details manually.',
        [
          'Create a QPO via the QR scan route on the supplier docket',
          'Record a chilled delivery temperature and confirm an accepted reading',
          'Enter received quantities and remove unwanted SKUs before submitting',
          'Create a QPO via Articlepedia by entering supplier details manually',
          'Enter driver name, employee code, vehicle number, and invoice number',
        ],
        10,
        { attempts: 'Up to 2 Attempts', tags: ['Mandatory'] },
      ),
      'ordering-lesson-4': lesson(
        'Handle advanced QPO scenarios: respond to out-of-range temperatures, park a QPO on hold mid-count, and process a delivery shortfall with vendor sign-off and a claim.',
        [
          'Respond to a rejected temperature (above 10°C) by retaking the reading at the dock',
          'Confirm a feedback-range temperature (7.1–10°C) and recognise the amber indicator',
          'Put a QPO on hold mid-count and resume it from My Orders',
          'Record a quantity shortfall when fewer items arrive than ordered',
          'Obtain vendor acknowledgement via digital signature before submitting',
          'Submit the QPO and raise a shortfall claim from the confirmation screen',
        ],
        10,
        { attempts: 'Up to 2 Attempts', tags: ['Mandatory'] },
      ),
      'ordering-lesson-5': lesson(
        'Navigate to My Orders from the Inventory Management menu, filter orders by date, and understand each order status — Pending Approval, Approved, Completed, and On Hold.',
        [
          'Navigate to My Orders via Inventory Management to view your orders',
          'Filtering view of Submitted Orders',
          'Understanding different order statuses',
        ],
        10,
        { tags: ['Mandatory'] },
      ),
    },
  },
  'receiving-essentials': {
    navId: 'receiving',
    subtitle: 'ASN & Dock Check',
    icon: 'Truck',
    accentColour: 'amber',
    moduleTag: 'Module 02 • Receiving',
    moduleColour: 'amber',
    moduleDescription:
      'Learn how to manage incoming deliveries from end to end — navigate the To Receive screen to locate purchase and delivery orders, scan item barcodes to record received quantities, handle temperature checks for chilled and frozen goods, verify the order, and generate a printed summary slip upon completion.',
    subModules: {
      'receiving-sandbox-tour': tour(
        'A quick orientation to the receiving sandbox — explore the key screens before you begin your first lesson.',
        3,
      ),
      'receiving-lesson-2': optionalLesson(
        'Navigate to the To Receive screen, use the date filter to view scheduled deliveries, search for a specific purchase or delivery order by number or warehouse, and apply vendor and warehouse filters to narrow down the order list.',
        [
          'Navigate to the To Receive screen via the Menu and Inventory Management features',
          "Identify today's scheduled deliveries using the default date filter",
          'Change the date filter to view deliveries on a different day',
          'Search for a specific order by PO number, DO number, or warehouse name and ID',
          'Filter the order list by vendor or warehouse using the Sort & Filter panel',
          'Open a delivery order to begin the receiving process',
        ],
        10,
      ),
      'receiving-lesson-3': optionalLesson(
        'Walk through the full goods receiving workflow — fill in delivery details, scan each item barcode, record the received quantities, verify the order, and print the summary slip.',
        [
          'Fill in driver name and vehicle number to begin a goods receiving transaction',
          'Open the barcode scanner from the GR input screen and scan items one by one',
          'Enter the received quantity for each scanned item using the GR QTY field',
          'Use Confirm and Scan Next to continue scanning, or Done Scanning to return to the full item list',
          'Mark all items as verified and proceed to the Goods Receiving Summary',
          'Confirm the goods receipt and print the summary slip from a nearby printer',
        ],
        15,
      ),
      'receiving-lesson-4': optionalLesson(
        'Learn how to handle deliveries with chilled and frozen goods — record class-level temperatures, respond to rejected and feedback-range readings, verify individual item temperatures, and complete the receiving process.',
        [
          'Identify the Temperature Check by Class card and understand how a class temperature applies to all items in that category',
          'Enter chilled and frozen temperatures and tap Set Temperatures to validate them',
          'Respond to a rejected temperature by retaking the measurement at the dock',
          'Confirm a feedback-range temperature and recognise the amber highlight on affected item cards',
          'Override individual item temperatures where the per-item reading differs from the class reading',
          'Mark each item as verified once quantity and temperature are confirmed',
          'Proceed to the Goods Receiving Summary, confirm the receipt, and print the summary slip',
        ],
        12,
      ),
      'receiving-lesson-5': optionalLesson(
        'Handle a goods receiving shortfall end to end — enter below-PO quantities, acknowledge the shortfall warning, obtain supplier confirmation via digital signature, confirm the goods receipt, and submit a shortfall claim for undelivered items.',
        [
          'Identify a quantity shortfall by entering a received quantity lower than the PO quantity',
          'Recognise the red highlight on shortfall items in the GR QTY field',
          'Acknowledge the Shortfall Items Detected warning and proceed to the summary screen',
          'Navigate the Goods Receiving Summary shortfall tab to review affected items',
          'Obtain a supplier digital signature via the Acknowledgement by supplier sheet',
          'Confirm the goods receipt after supplier acknowledgement is recorded',
          'Navigate to Shortfall Claims and submit the claim for undelivered items',
        ],
        12,
      ),
      'receiving-lesson-6': optionalLesson(
        'Learn how to receive the first shipment of a warehouse Delivery Order (DO) arriving across multiple trucks. Record the shipment details and pallet counts, scan the truck ID, confirm the pallets received, and hold the DO if more shipments are expected.',
        [
          'Identify a warehouse Delivery Order (DO) arriving in multiple shipments.',
          'Enter the total DO pallet count and record the shipment details.',
          'Key in received and dispatch pallet counts by color',
          'Scan the truck ID and confirm the pallets received.',
          'Hold the DO if more shipments are expected.',
        ],
        12,
      ),
      'receiving-lesson-7': optionalLesson(
        'Learn how to receive the final shipment of a held warehouse Delivery Order (DO). Reopen the held DO, record the final shipment details, check temperature-sensitive goods when required, verify the pallets received, scan the truck ID, and complete the goods receipt.',
        [
          'Reopen a held Delivery Order (DO) from the To Receive list.',
          "Record the final shipment's driver, vehicle, and pallet count.",
          'Record a temperature reading for temperature-sensitive goods when required.',
          'Key in received and dispatch pallet counts by color and scan the truck ID.',
          'Confirm the goods receipt and complete the process with a summary slip',
        ],
        10,
      ),
      // Absorbed the old receiving-lesson-1 ("Looking up a delivery in History"), which the
      // client flagged as a duplicate of this one. It inherits that lesson's `required`
      // status: it is now the only mandatory Receiving lesson, and without this the module
      // would drop out of Required Training entirely.
      'receiving-lesson-8': lesson(
        'Find a past delivery in History and review the full order record: driver, vehicle, totals, and line-by-line item quantities, then recognise the document statuses you will see on the list.',
        [
          'Open History from the Manage Inventory menu',
          'Search the history list by vendor name or order number',
          'Open an order record and read the delivery summary',
          'Expand the item list and check PO vs received quantities',
          'Recognise the Pending, Auto-Closed and Failed document statuses',
        ],
        7,
        { passingScore: 75, tags: ['Mandatory'] },
      ),
    },
  },
  'stocktake-essentials': {
    navId: 'stocktake',
    subtitle: 'Cycle Counts',
    icon: 'Package2',
    accentColour: 'indigo',
    moduleTag: 'Module 03 • Stocktake',
    moduleColour: 'indigo',
    moduleDescription:
      'Master the cycle count process: start a count, scan and record quantities, review variances, and submit results.',
    subModules: {
      'stocktake-sandbox-tour': tour(
        'A quick orientation to the stocktake sandbox — explore the key screens before you begin your first lesson.',
        3,
      ),
      'stocktake-lesson-1a': lesson(
        'Learn how to navigate to the Stocktake screen, review the Setup tab details, monitor count progress using the Progress tab, and start the stocktake.',
        [
          'Navigate to the Stocktake screen via the Menu',
          'Review the Setup tab — date, location count, and editing max locations',
          'Switch to the Progress tab to monitor counting progress',
          'Start the stocktake to begin scanning',
        ],
        5,
        { tags: ['stocktake', 'orientation'] },
      ),
      'stocktake-lesson-1b': optionalLesson(
        'Learn how to navigate to the Stocktake screen, understand what the screen looks like when no stocktake is scheduled, and start a stocktake when one is available.',
        [
          'Navigate to the Stocktake screen via the Menu',
          'Recognise the no-stocktake state and understand when stocktakes are scheduled',
          'Start the stocktake when a stocktake day is active',
        ],
        5,
        { tags: ['stocktake', 'orientation'] },
      ),
      'stocktake-lesson-2a': lesson(
        'Complete a full stocktake cycle — navigate to the Stocktake screen, handle a failed location scan by entering the code manually, count products using the barcode scanner, and submit the scanned list to your Branch Manager.',
        [
          'Navigate to the Stocktake screen via the Menu and identify a scheduled stocktake day',
          'Start a stocktake count and understand the scan-location workflow',
          'Recover from a failed barcode scan by entering the 4-digit location code manually',
          'Scan the location barcode directly for the normal, successful scan scenario',
          'Scan product barcodes to identify SKUs and enter counted quantities',
          'Use "Add and Scan Another" to continue counting multiple products in a location',
          'Review the Scanned List and submit the completed count to the system',
        ],
        8,
        { tags: ['Mandatory'] },
      ),
      'stocktake-lesson-2b': lesson(
        'Complete a full stocktake cycle — navigate to the Setup screen, start the stocktake from the Progress tab, scan a location, count products using the barcode scanner, and submit the scanned list.',
        [
          'Navigate to the Stocktake screen via the Menu and identify a scheduled stocktake day',
          'Start a stocktake count from the Progress tab and understand the scan-location workflow',
          'Recover from a failed barcode scan by entering the 4-digit location code manually',
          'Scan the location barcode directly for the normal, successful scan scenario',
          'Scan product barcodes to identify SKUs and enter counted quantities',
          'Use "Add and Scan Another" to continue counting multiple products in a location',
          'Review the Scanned List and submit the completed count to the system',
        ],
        8,
        { tags: ['Mandatory'] },
      ),
      'stocktake-lesson-3': lesson(
        'Handle four common stocktake exceptions — a location already counted, a product scanned twice, a product outside the current cycle, and an unrecognised barcode resolved via manual search.',
        [
          'Recognise and respond to a "Location Already Counted" alert during scanning',
          'Choose between amending an existing count and scanning a different location',
          'Identify a duplicate scan notification and update a previously recorded quantity',
          'Review the pre-filled previous count and enter a corrected figure',
          'Dismiss a "Product Not in This Stocktake" alert and continue scanning',
          'Use the manual product search to locate an item when the barcode is unrecognised',
          'Select a search result, enter the counted quantity, and add it to the scanned list',
          'Review the completed list and submit it to the system',
        ],
        10,
        { tags: ['Mandatory'] },
      ),
      'stocktake-lesson-4': lesson(
        'Prepare for stocktake day as a Branch Manager — review and update the max locations in Setup, observe the validation constraint, monitor team progress, inspect unscanned locations, and approve the completed count.',
        [
          'Review the Stocktake Setup tab and verify the scheduled date and location count',
          'Edit the max locations value and understand the minimum-count validation rule',
          'Save the updated max locations and confirm the change before stocktake begins',
          'Switch to the Progress tab and monitor scanning progress on stocktake day',
          'Review counted quantity and value by location and spot figures that need a recount',
          'Identify unscanned locations using the progress banner and See All panel',
          'Approve the completed stocktake and finalise the count',
        ],
        8,
        { tags: ['Mandatory'] },
      ),
    },
  },
  'returns-essentials': {
    navId: 'returns',
    subtitle: 'Vendor & Customer',
    icon: 'CornerDownLeft',
    accentColour: 'rose',
    moduleTag: 'Module 04 • Returns',
    moduleColour: 'rose',
    moduleDescription: 'Handling RSTOs and RTVs.',
    subModules: {
      'returns-sandbox-tour': tour(
        'A quick orientation to the returns sandbox — explore the key screens before you begin your first lesson.',
        3,
      ),
      'returns-lesson-1': optionalLesson(
        'Learn how to create and search for a Return to Vendor (RTV).',
        [
          'How to start a RTV',
          "How to review a SKU's return history",
          'How to submit the RTV',
          'Recognise and understand error messages',
        ],
        12,
      ),
      'returns-lesson-2': optionalLesson(
        'Learn how to search for the RTV and perform Goods Issue (GI).',
        [
          'Identify RTVs by their status — Pending disposal or Issue goods',
          'Complete Goods Issue for a RTV',
          'Complete Disposal for a RTV',
          'Zerorise the Goods Issue to cancel the RTV and reissue',
        ],
        15,
      ),
      'returns-lesson-3': optionalLesson(
        'Walk through two RSTO scenarios: a standard return processed through Issue Goods with vendor acknowledgement, then an Excess Stock return where you handle the expiry date warning and track the return while it awaits approval.',
        [
          'Create a normal RSTO and complete Goods Issue',
          'Create an RSTO under Excess Stock Returns',
          'Understand the different statuses for RSTOs',
        ],
        20,
      ),
      'returns-lesson-4': optionalLesson(
        "Find a past return in History under the Goods Issued tab, filter the list by status, and read an RTV record's historical detail.",
        [
          'Navigate to History and switch to the Goods Issued tab',
          'Recognise the statuses a completed return can carry',
          'Filter completed returns by status, vendor, and date',
          "Read an RTV record's historical detail",
        ],
        8,
      ),
    },
  },
  'soh-adjustment': {
    navId: 'soh',
    comingSoon: false,
    subtitle: 'Inventory Adjustments',
    icon: 'SlidersHorizontal',
    accentColour: 'purple',
    moduleTag: 'Module 05 • SOH Adjustments',
    moduleColour: 'indigo',
    moduleDescription:
      'Learn to correct stock-on-hand figures: locate the adjustment screen, apply positive and negative adjustments with the right reason codes, and understand the audit trail.',
    subModules: {
      'soh-sandbox-tour': tour(
        'A quick orientation to the SOH adjustments sandbox — explore the key screens before you begin your first lesson.',
        3,
      ),
      'soh-lesson-1': optionalLesson(
        'Navigate to the Adjustments screen via the Menu, search for an adjustment using the search bar, and filter the adjustments list by type, status, and creator to find specific records.',
        [
          'Open the Adjustments screen from the Menu under Manage Inventory',
          'Use the search bar to filter adjustments by article number or description',
          'Use the filtering to narrow down adjustments list by type, status, and creator',
          'Understand how adjustments are displayed',
        ],
        5,
      ),
      'soh-lesson-2': optionalLesson(
        'Walk through creating a Write Off adjustment end-to-end: navigate from Adjustments list, scan a product barcode or search by SKU number/description, select the correct adjustment type, enter the quantity and select unit of measure, choose a reason code, and submit for approval.',
        [
          'Create a new inventory adjustment from the Adjustments screen',
          'Scan a product barcode or search by SKU number/description to open SOH adjustment form',
          'Understand different reason codes for Write off',
          'Submit the adjustment and recognise the Pending approval status',
        ],
        10,
      ),
      'soh-lesson-3': optionalLesson(
        'Walk through creating a Stocks for own use adjustment end-to-end: navigate from Adjustments list, scan a product barcode or search by SKU number/description, select the correct adjustment type, enter the quantity and select unit of measure, choose a reason code, and submit for approval.',
        [
          'Create a new inventory adjustment from the Adjustments screen',
          'Scan a product barcode or search by SKU number/description to open SOH adjustment form',
          'Understand different reason codes for Stocks for own use',
          'Submit the adjustment and recognise the Pending approval status',
        ],
        12,
      ),
      'soh-lesson-4': optionalLesson(
        'Walk through creating a Stocks for own use adjustment end-to-end: navigate from Adjustments list, scan a product barcode or search by SKU number/description, select the correct adjustment type, enter the quantity and select unit of measure, choose a reason code, add an optional remark, and submit for approval.',
        [
          'Create a new inventory adjustment from the Adjustments screen',
          'Scan a product barcode or search by SKU number/description to open SOH adjustment form',
          'Understand different reason codes for Free goods',
          'Submit the adjustment and recognise the Pending approval status',
        ],
        12,
      ),
      'soh-lesson-5': optionalLesson(
        'Learn how to create a Break Bulk SOH adjustment: scan/search a parent product, select the correct adjustment type, scan/search a child product, enter the correct ratio and parent quantity. You will also learn the common errors that prevent a Break Bulk adjustment submission.',
        [
          'Create a new Break Bulk adjustment type from the Adjustments screen and understand when it applies',
          'Scan Parent and Child SKUs',
          'Identify the Child article ratio to Parent quantity fields and enter the correct values',
          'Recognise the different errors for Break Bulk and understand how to resolve them',
        ],
        12,
      ),
      'soh-lesson-6': optionalLesson(
        'Learn how to create a SOH correction: navigate from Adjustments list, scan a product barcode or search by SKU number/description, select the correct adjustment type, enter the physical count quantity and select unit of measure, choose a reason code, add an optional remark, and submit for approval. ',
        [
          'Select SOH Correction as the adjustment type for reconciling system SOH against a physical count',
          'Enter the correct quantity for physical count',
          'Distinguish between different SOH correction reasons: Unaccountable gain/loss, Count issue, Delivery discrepancy (NEW)',
          'Understand the role-dependent approval routing',
        ],
        12,
      ),
      'soh-lesson-7': optionalLesson(
        'Learn to add a second item (with same adjustment type) or a new item (with different adjustment type) within a single submission, and submit all the adjustments at one go.',
        [
          'Use Add more items to add multiple SKUs/adjustment types',
          'Review the SKU list as each item is added',
          'Set the adjustment type, quantity and reason for individual items',
        ],
        8,
      ),
      'soh-lesson-8': optionalLesson(
        "Navigate to the History screen's Adjustments tab, review the list of past adjustments, use the status filter chips and advanced filter panel find specific adjustments, and open an adjustment card to view its full summary.",
        [
          'Navigate to the History screen and switch to the Adjustments tab',
          'Identify the key fields in each adjustment card',
          'Use the status filter chips to quickly narrow the adjustment list by a single adjustment status',
          'Use the advanced filter panel to narrow the adjustment list using multiple filters simultaneously',
          'Tap a card to open the Adjustment Summary and review the complete adjustment record',
        ],
        5,
      ),
    },
  },
  'expiry-check': {
    navId: 'expiry',
    subtitle: 'Expiry Dates',
    icon: 'CalendarX2',
    accentColour: 'red',
    moduleTag: 'Module 06 • Expiry Check',
    moduleColour: 'rose',
    moduleDescription:
      'Work the expiry check end to end: read the list of stock that has expired, is expiring, or has no date recorded; narrow a long list by filter, scan or search; then key in a missing date and update stock that is no longer on the shelf. From there, Expiry Check hands over to write-off or returns.',
    subModules: {
      'expiry-sandbox-tour': tour(
        'A quick orientation to the expiry check sandbox — explore the key screens before you begin your first lesson.',
        3,
      ),
      'expiry-lesson-1': optionalLesson(
        'Open Expiry Check from the Menu and read both lists — stock that has expired or is expiring, and stock the warehouse sent across with no expiry date recorded.',
        [
          'Navigate to Expiry Check from the Menu and the Features screen',
          'Read a product card: product description, class, SKU, EAN and vendor/WH code',
          'Use the red date badge to decide which item to check first',
          'Switch to the Missing Expiry Date tab to key in a missing expiry date',
          'Recognise what an empty list looks like',
        ],
        8,
      ),
      'expiry-lesson-2': optionalLesson(
        'Find the item in your hand on a long expiry list: scan the barcode, or key in the product SKU when the label is too damaged to read.',
        [
          'Locate the filter on the expired and expiring list and know what it narrows by',
          'Scan a product barcode to go straight to the item expiry detail page',
          'Fall back to searching by SKU when the barcode will not scan',
          'Resolve a SKU tied to multiple EANs by scanning the EAN or selecting the right one',
        ],
        9,
      ),
      'expiry-lesson-3': optionalLesson(
        'Act on an item: record an expiry date for stock that has none recorded, update stock that has already sold or expired, and choose between a write-off and a return.',
        [
          'Add a missing expiry date for stock that has no date recorded',
          'Update an item that is not found on the shelf to sold/withdrawn',
          'Choose between write-off and returns after updating an item to sold/withdrawn',
          'Recognise where Expiry Check leads you when you choose write-off or returns',
        ],
        12,
      ),
    },
  },
  'task-management': {
    navId: 'tasks',
    subtitle: 'Daily Shift Queues',
    icon: 'ListChecks',
    accentColour: 'teal',
    moduleTag: 'Module 07 • Task Management',
    moduleColour: 'blue',
    moduleDescription:
      'Manage daily shift queues: view, assign, prioritise, and close tasks so nothing falls through the cracks during a busy shift.',
    subModules: {
      'task-management-sandbox-tour': tour(
        'A quick orientation to the task management sandbox — explore the key screens before you begin your first lesson.',
        3,
      ),
      'task-management-lesson-1a': optionalLesson(
        'Learn how to navigate the Task page, use the date filter to view tasks for a specific day, open the full task list with See all tasks, and understand the available filters and task statuses.',
        [
          'Open the Task page and switch between My Tasks and Store Tasks',
          'Use the date filter to view tasks scheduled for a specific day',
          'Open the full task list by tapping See all tasks',
          'Understand the filter options and task statuses (Not Started, In Progress, Overdue, Completed, Shared)',
        ],
        6,
      ),
      // Same paragraph as 1a, but bullet 1 is deliberately NOT the client's
      // "switch between My Tasks and Store Tasks": Store Tasks is gated to
      // BM/TL/DM/PH in `task-roles.ts`, so an RA/SK learner reading this lesson
      // has a single tab and no control to switch to.
      'task-management-lesson-1b': optionalLesson(
        'Learn how to navigate the Task page, use the date filter to view tasks for a specific day, open the full task list with See all tasks, and understand the available filters and task statuses.',
        [
          'Open the Task page and recognise that My Tasks is your view — Store Tasks is a Manager and Team Leader view',
          'Use the date filter to view tasks scheduled for a specific day',
          'Open the full task list by tapping See all tasks',
          'Understand the filter options and task statuses (Not Started, In Progress, Overdue, Completed, Shared)',
        ],
        6,
      ),
      'task-management-lesson-2': optionalLesson(
        "Learn how to create and manage tasks: create individual and shared tasks, assign them to staff, and edit or close tasks when needed. You'll also learn how shared tasks work and when a task can be edited or closed.",
        [
          'Create an individual task and assign it to staff',
          'Create a shared task and understand how shared tasks work',
          'Select the assignment type and choose the assignees',
          'Edit a task before it starts and understand that only the creator can edit it',
          'Close a task with a reason before it starts and understand that only the creator can close it',
        ],
        12,
      ),
      'task-management-lesson-3a': optionalLesson(
        'Learn how to complete individual and shared tasks from start to finish: review the task details, start the task, add a remark, optionally attach a photo, and submit the task. Managers and Team Leaders will also learn how to track staff progress.',
        [
          "Review a task's details and start the task",
          'Complete an individual task by adding a remark, optionally attaching a photo, and submitting it',
          'Complete a shared task and understand how task ownership works once the task is started',
          'View the completion acknowledgement and return to the Task page',
          'As a Manager or Team Leader, track staff progress for individual and shared tasks',
        ],
        12,
      ),
      // 3b is the RA/SK cut: no `bm-tl-view` section, so the staff-progress
      // bullet and the manager clause are both left off deliberately.
      'task-management-lesson-3b': optionalLesson(
        'Learn how to complete individual and shared tasks from start to finish: review the task details, start the task, add a remark, optionally attach a photo, and submit the task.',
        [
          "Review a task's details and start the task",
          'Complete an individual task by adding a remark, optionally attaching a photo, and submitting it',
          'Complete a shared task and understand how task ownership works once the task is started',
          'View the completion acknowledgement and return to the Task page',
        ],
        12,
      ),
    },
  },
}

/** Reverse-lookup: find a registry entry by its navId (route param). */
export function findRegistryByNavId(navId: string): [string, ModuleRegistryEntry] | undefined {
  return Object.entries(MODULE_REGISTRY).find(([, entry]) => entry.navId === navId)
}
